// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.
import { useEffect, useState } from 'react';
import { HOST_URL } from '@/config.ts';
import type { OnlineUser } from '@/components/presence/presence-list';
import { useServerNotificationMute } from '@/hooks/use-server-notifications-mute.ts';
import { showNotification } from '@/lib/notifications';

interface ChannelVoiceNotification {
  notifications: boolean; // from settings
  voiceChannelUsers: OnlineUser[];
  id: string; // server ID
  name: string; // channel Name
  userId: string;
}

export const useVoiceChannelNotification = ({
  notifications,
  voiceChannelUsers,
  id,
  name,
  userId,
}: ChannelVoiceNotification) => {
  const [previousUsers, setPreviousUsers] = useState<OnlineUser[]>([]);
  const { allowNotify } = useServerNotificationMute(id);

  useEffect(() => {
    if (!allowNotify) return;
    if (!notifications) return;
    const currentUsers = voiceChannelUsers;

    const newUsers = currentUsers.filter(
      (currentUser) =>
        !previousUsers.some(
          (prevUser) => prevUser.userId === currentUser.userId
        ) && currentUser.userId !== userId // Don't notify for self
    );

    if (newUsers.length > 0) {
      const lastConnectedUser = [...newUsers]
        .sort((a, b) => {
          const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return aTime - bTime;
        })
        .at(-1);

      const freshDevice = lastConnectedUser?.devices
        ?.slice()
        .sort((a, b) => {
          const aTime = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
          const bTime = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
          return aTime - bTime;
        })
        .at(-1);

      const channelName = freshDevice?.channelName ?? 'general';

      try {
        if (!notifications) return;
        const sendBrowserNotification = async () => {
          if (!('Notification' in window)) {
            console.log('This browser does not support notifications');
            return;
          }

          const showBrowserNotification = () => {
            // new Notification(`${name}`, {
            //   body: `Пользователь ${lastConnectedUser?.name} подключился к каналу - ${channelName}`,
            //   icon: `${HOST_URL}/favicon-96x96.png`,
            // });
            showNotification(`${name}`, {
              body: `Пользователь ${lastConnectedUser?.name} подключился к каналу - ${channelName}`,
              icon: `${HOST_URL}/favicon-96x96.png`,
            });
          };

          if (!('Notification' in window)) {
            console.log('This browser does not support notifications');
            return;
          }
          if (Notification.permission === 'granted') {
            showBrowserNotification();
          } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then((permission) => {
              if (permission === 'granted') showBrowserNotification();
            });
          }
        };

        sendBrowserNotification(); // fallback to browser default
      } catch (error) {
        console.error('Push notification failed', error);
      }
    }

    setPreviousUsers(currentUsers);
  }, [notifications, id, name, userId, voiceChannelUsers]);
};
